import React from 'react';
import { graphql } from 'gatsby';
import Container from '@atoms/Container';
// import Breadcrumbs from '@sections/Breadcrumbs';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import { colors } from '../primitives/colors';
import { StyledButtonAsLink } from '../primitives/styled-button';

const SplitTextBanner = ({ data }) => {
    const { settings, headlines, left, right } = data;
    const { headline: titleHeadline, subheadline: titleSubheadline } = headlines;
    const { headline: leftHeadline, copy: leftCopy, link: leftLink } = left;
    const { headline: rightHeadline, copy: rightCopy, link: rightLink } = right;

    // the breadcrumbs component return nothing always. 
    // I'll leave it here in just in case we decide to change it. 
    const hasBreadcrumbs = false && !!settings?.has_breadcrumbs;
    const hideBackground = !!settings?.hide_background;
    const darkMode = !!settings?.dark_mode;

    return (
        <div css={styles.background(hideBackground, darkMode)}>
            <Container css={styles.container(hasBreadcrumbs)}>
                {/* {hasBreadcrumbs && (
                    <div css={styles.breadcrumbsWrap}>
                        <Breadcrumbs />
                    </div>
                )} */}
                <div css={styles.headlines}>
                    {!!titleHeadline && (
                        <h2 css={styles.headline}>
                            {titleHeadline}
                        </h2>
                    )}
                    {!!titleSubheadline && (
                        <div
                            css={[RichTextEditorStyles, styles.subheadline]}
                            dangerouslySetInnerHTML={{ __html: titleSubheadline }}
                        />
                    )}
                </div>
                <div css={styles.contentWrap}>
                    <div
                        css={[styles.splitCol, styles.splitColLeft]}
                    >
                        <h3 css={[styles.colHeadline, styles.colHeadlineLeft]}>
                            {apCaseOnlyTitleTags(leftHeadline)}
                        </h3>
                        <div
                            css={[RichTextEditorStyles, styles.copy]}
                            dangerouslySetInnerHTML={{ __html: leftCopy }}
                        />
                        {!!leftLink?.href && (
                            <div css={styles.buttonWrap}>
                                <StyledButtonAsLink to={leftLink.href}>{leftLink.title}</StyledButtonAsLink>
                            </div>
                        )}
                    </div>

                    <div
                        css={[styles.splitCol, styles.splitColRight]}
                    >
                        <h3 css={styles.colHeadline}>
                            {apCaseOnlyTitleTags(rightHeadline)}
                        </h3>
                        <div
                            css={[RichTextEditorStyles, styles.copy]}
                            dangerouslySetInnerHTML={{ __html: rightCopy }}
                        />
                        {!!rightLink?.href && (
                            <div css={styles.buttonWrap}>
                                <StyledButtonAsLink to={rightLink.href}>{rightLink.title}</StyledButtonAsLink>
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </div>
    );
};

const styles = {
    background: (isHideBackground, isDarkMode) => ({
        backgroundColor: isDarkMode ? colors.shadesBlack : isHideBackground ? colors.shadesWhite : colors.grey100,
        color: isDarkMode && colors.shadesWhite,
    }),
    container: (hasBreadcrumbs) => ({
        paddingTop: hasBreadcrumbs ? "0.5rem" : "2.5rem",
        paddingBottom: hasBreadcrumbs ? "1rem" : "2.5rem",
        "@media (min-width: 768px)": {
            paddingTop: hasBreadcrumbs ? "1.5rem" : "4rem",
            paddingBottom: hasBreadcrumbs ? "4rem" : "4rem",
        }
    }),
    breadcrumbsWrap: {
        marginBottom: "0.75rem"
    },
    headlines: {
        marginBottom: "4rem",
    },
    headline: {
        marginBottom: "1.25rem",
        fontSize: "2.25rem",
        lineHeight: 1,
        textAlign: "center",
        marginRight: "auto",
        marginLeft: "auto",
        "@media (min-width: 1024px)": {
            fontSize: "54px",
        }
    },
    subheadline: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        textAlign: "center",
        "@media (min-width: 1024px)": {
            maxWidth: "48rem",
            fontSize: "1.25rem",
            lineHeight: "1.75rem",
            marginRight: "auto",
            marginLeft: "auto",
        }
    },
    contentWrap: {
        display: "flex",
        position: "relative",
        flexDirection: "column",
        flexWrap: "nowrap",
        marginRight: "auto",
        marginLeft: "auto",
        "@media (min-width: 1280px)": {
            flexDirection: "row",
            alignItems: 'baseline',
        }
    },
    splitColLeft: {
        marginBottom: "2rem",
        "@media (min-width: 1280px)": {
            paddingRight: "2rem",
        }
    },
    splitColRight: {
        "@media (min-width: 1280px)": {
            paddingLeft: "2rem",
        }
    },
    splitCol: {
        display: "flex",
        position: "relative",
        flexDirection: "column",
        marginRight: "auto",
        marginLeft: "auto",
        "@media (min-width: 1280px)": {
            margin: "0",
            width: "50%",
            flexShrink: 0,
        }
    },
    colHeadline: {
        marginBottom: "1.25rem",
        fontSize: "2.25rem",
        lineHeight: 1,
        marginBottom: "1.25rem",
        marginRight: "auto",
        marginLeft: "auto",
        color: colors.primaryOrange,
        "@media (min-width: 1024px)": {
            fontSize: "36px",
        }
    },
    colHeadlineLeft: {
        marginTop: "1.25rem",
    },
    copy: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        'p a img': {
            width: "100%",
            maxWidth: "150px",
        },
        "@media (min-width: 1024px)": {
            maxWidth: "48rem",
            fontSize: "1.25rem",
            lineHeight: "1.75rem",
            marginBottom: "1rem",
        }
    },
    buttonWrap: {
        display: "flex",
        marginTop: "2rem",
        "@media (min-width: 1024px)": {
            marginTop: "auto",
        }
    }
};

export const query = graphql`
  fragment SplitTextBannerData on CmsPageBuilderBlocks {
    split_text_banner {
      settings {
        dark_mode
        has_breadcrumbs
        hide_background
      }
      headlines {
        headline
        subheadline
      }
      left {
        headline
        copy
        link {
          href
          title
        }
      }
      right {
        headline
        copy
        link {
          href
          title
        }
      }
    }
  }
`

export default SplitTextBanner
